import { useState } from 'react';
import { Textarea } from '@chakra-ui/react';
import { Button, Colors, Text } from '@mercator-gcp/grout';
import BasicModal from 'components/elements/BasicModal';
import { FlexColumn } from 'components/layout/FlexColumn/FlexColumn';
import { useSendFeedback } from 'hooks/useSendFeedback';
import { useUser } from 'state/auth';

type SuggestCorrectionModalProps = {
  isOpen: boolean;
  onClose: () => void;
  projectId?: number;
  companyId?: string;
};

const commentsLabelStyle = {
  marginBottom: 'padding_8',
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
};

const textareaContainerStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'padding_8',
  width: '100%',
};

const textareaStyle = {
  border: `2px solid ${Colors.ArmyGreen300}`,
  borderRadius: '6px',
  padding: 'padding_4 padding_8',
  minHeight: '64px',
  fontSize: '13px',
  _focus: {
    border: `2px solid ${Colors.ArmyGreen300}`,
    outline: 'none',
  },
  _placeholder: {
    color: Colors.ArmyGreen300,
  },
};

const SuggestCorrectionModal = ({
  isOpen,
  onClose,
  projectId,
  companyId,
}: SuggestCorrectionModalProps) => {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sendFeedback } = useSendFeedback();
  const { user } = useUser();

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      return;
    }

    setIsSubmitting(true);
    const pageUrl = window.location.href;
    const success = await sendFeedback({
      feedback: feedback.trim(),
      project_id: projectId,
      company_id: companyId,
      page_url: pageUrl,
      user_email: user?.email ?? '',
    });

    if (success) {
      setFeedback('');
      onClose();
    }
    setIsSubmitting(false);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFeedback('');
      onClose();
    }
  };

  return (
    <BasicModal isOpen={isOpen} onClose={handleClose}>
      <BasicModal.Body title="Provide feedback about this page">
        <FlexColumn sx={textareaContainerStyle}>
          <Text.SmallLabel sx={commentsLabelStyle} color={Colors.Black800}>
            Comments
          </Text.SmallLabel>
          <Textarea
            placeholder="Please be as details as possible"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            sx={textareaStyle}
            resize="vertical"
            disabled={isSubmitting}
          />
        </FlexColumn>
      </BasicModal.Body>
      <BasicModal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!feedback.trim() || isSubmitting}>
          {isSubmitting ? 'Submitting...' : 'Submit'}
        </Button>
      </BasicModal.Footer>
    </BasicModal>
  );
};

export default SuggestCorrectionModal;
